import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  QrCode, Copy, CheckCircle2, Download, Loader2, AlertCircle,
  Maximize2, X,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { fetchTastingFeedbackLink } from "../lib/api";
import { toast } from "sonner";

/**
 * QR + URL display for the anonymous feedback link. Designed so a
 * dispatcher/lead-host can either:
 *   1. Scan the QR straight into a digital picture frame at the bus, OR
 *   2. Copy the URL into the route email for the host crew.
 * Heavy intentional sizing — the QR needs to be big enough to scan
 * from a few feet away on a picture frame.
 */
export function FeedbackQRDialog({ open, onOpenChange, tasting }) {
  const [data, setData] = useState(null);     // { url, qr_png_data_uri } | "404" | null
  const [copied, setCopied] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    if (!open || !tasting?.id) return;
    setData(null);
    setCopied(false);
    setFullscreen(false);
    fetchTastingFeedbackLink(tasting.id)
      .then(setData)
      .catch(() => setData("error"));
  }, [open, tasting?.id]);

  // ESC out of fullscreen mode (touch-safe — also Lock screen on mobile)
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);

  const copy = async () => {
    if (!data?.url) return;
    try {
      await navigator.clipboard.writeText(data.url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — long-press the link to copy manually");
    }
  };

  const downloadQR = () => {
    if (!data?.qr_png_data_uri) return;
    const a = document.createElement("a");
    a.href = data.qr_png_data_uri;
    const safeName = (tasting?.dispensary_name || "bus-event")
      .replace(/[^a-z0-9]+/gi, "_")
      .toLowerCase();
    a.download = `feedback-qr-${safeName}-${tasting?.scheduled_date || "event"}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="feedback-qr-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-emerald-400" />
            Feedback QR
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Load this onto the picture frame at the bus exit so buyers
            can scan as they leave. Active from now until the event is
            cancelled.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-xs text-zinc-300 bg-zinc-900/60 border border-zinc-800 rounded-md p-3">
            <div className="font-medium text-zinc-100">{tasting?.dispensary_name}</div>
            {tasting?.scheduled_date && (
              <div className="text-zinc-500 font-mono text-[11px]">
                {new Date(tasting.scheduled_date + "T00:00:00").toLocaleDateString("en-US", {
                  weekday: "long", month: "short", day: "numeric",
                })}
              </div>
            )}
          </div>

          {data === null ? (
            <div className="flex items-center justify-center h-64 bg-zinc-900/40 rounded-xl border border-zinc-800">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
            </div>
          ) : data === "error" ? (
            <div className="flex items-center gap-2 text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md p-3 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />
              Couldn't load the QR. Try closing and reopening this dialog.
            </div>
          ) : (
            <>
              {/* Big white QR card — high contrast for picture-frame scan */}
              <div
                data-testid="feedback-qr-image"
                className="bg-white rounded-xl p-6 flex items-center justify-center"
              >
                <img
                  src={data.qr_png_data_uri}
                  alt="Scan to leave feedback"
                  className="w-56 h-56"
                />
              </div>

              <div className="space-y-2">
                <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
                  Public feedback URL
                </div>
                <div
                  data-testid="feedback-url"
                  className="text-xs font-mono text-zinc-300 break-all bg-zinc-900/60 border border-zinc-800 rounded p-2"
                >
                  {data.url}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button
                  data-testid="feedback-show-fullscreen"
                  onClick={() => {
                    // Close the underlying Radix Dialog so its overlay
                    // doesn't intercept taps over our fullscreen view.
                    setFullscreen(true);
                    onOpenChange(false);
                  }}
                  className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 h-10 col-span-2"
                  title="Show big QR — use when the picture frame fails and you need to display from your phone"
                >
                  <Maximize2 className="w-4 h-4 mr-2" />
                  Show fullscreen
                </Button>
                <Button
                  data-testid="feedback-copy-url"
                  variant="outline"
                  onClick={copy}
                  className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800"
                >
                  {copied ? (
                    <CheckCircle2 className="w-4 h-4 mr-1 text-emerald-400" />
                  ) : (
                    <Copy className="w-4 h-4 mr-1" />
                  )}
                  <span className="text-xs">Copy URL</span>
                </Button>
                <Button
                  data-testid="feedback-download-qr"
                  variant="outline"
                  onClick={downloadQR}
                  className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800"
                >
                  <Download className="w-4 h-4 mr-1" />
                  <span className="text-xs">Download QR</span>
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>

    {/* Fullscreen QR overlay — rendered as a portal directly on
        document.body so it escapes the Dialog's overlay stacking
        context. Used by drivers/hosts to display the QR from a
        phone when the picture frame fails. Tap anywhere to dismiss. */}
    {fullscreen && data && data !== "error" && createPortal(
        <div
          data-testid="feedback-fullscreen-overlay"
          onClick={() => setFullscreen(false)}
          className="fixed inset-0 z-[200] bg-black flex flex-col items-center justify-center px-6 py-10 cursor-pointer"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setFullscreen(false); }}
            data-testid="feedback-fullscreen-close"
            className="absolute top-5 right-5 text-zinc-400 hover:text-zinc-100 p-2"
            aria-label="Close fullscreen"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-emerald-300/80 mb-3">
            Scan to share your feedback
          </div>

          <div className="bg-white rounded-2xl p-6 sm:p-10 shadow-2xl">
            <img
              src={data.qr_png_data_uri}
              alt="Scan for feedback"
              className="w-[280px] h-[280px] sm:w-[420px] sm:h-[420px]"
            />
          </div>

          <div className="text-center mt-8 max-w-md">
            <div className="text-xl sm:text-2xl font-semibold text-zinc-100 mb-1">
              {tasting?.dispensary_name}
            </div>
            {tasting?.scheduled_date && (
              <div className="text-sm font-mono text-zinc-500">
                {new Date(tasting.scheduled_date + "T00:00:00").toLocaleDateString("en-US", {
                  weekday: "long", month: "long", day: "numeric",
                })}
              </div>
            )}
            <div className="mt-6 text-[11px] uppercase tracking-[0.18em] font-mono text-emerald-300/80">
              The Original Jack Herer
            </div>
            <div className="text-[10px] uppercase tracking-[0.15em] font-mono text-zinc-700 mt-0.5">
              Jack Enterprises
            </div>
          </div>

          <div className="mt-8 text-[10px] uppercase tracking-wider font-mono text-zinc-700">
            tap anywhere to close
          </div>
        </div>,
        document.body
    )}
    </>
  );
}

export default FeedbackQRDialog;
